import { inject } from "mobx-react";
import { useMemo } from "react";

import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import type { FeedResultFragment } from "@ihr-radioedit/inferno-webapi";
import { formatImage } from "@ihr-radioedit/inferno-core";
import { Heading, MagicLink } from "../../../../core/ui";
import { getCoastItemDate } from "../../lib";
import { CoastFeedItemType, getCoastFeedImg } from "../../lib/coastFeed";
import { CoastLinkedGuests } from "../CoastLinkedGuests.component";
import { CoastLinkedHost } from "../CoastLinkedHost.component";
import { isPublishRecord, RESPONSIVE_IMAGE, reverseRoute } from "@inferno/renderer-shared-core";
import type { Store } from "@inferno/renderer-shared-core";

interface CoastFeedShowItemProps {
  item: FeedResultFragment;
  store?: Store;
}

export const CoastFeedUpcomingShowItem = inject("store")(({ item, store }: CoastFeedShowItemProps) => {
  if (!store || !isPublishRecord(item.record)) {
    return null;
  }

  const { site } = store;
  const { slug, payload } = item.record;
  const { summary } = payload;

  const showUrl = reverseRoute(site, "shows_detail", { slug }) || "#";

  const thumb = formatImage(summary.image.url, store.env.IMAGE_HOST);

  const { imgSrc, srcset } = getCoastFeedImg(thumb, CoastFeedItemType.UPCOMING_SHOWS, { quality: 80 });

  const formattedDate = useMemo(
    () => getCoastItemDate(site.index.timeZone || store.env.DEFAULT_TIMEZONE, "EEEE, MMMM d, yyyy", item),
    [item, store.env.DEFAULT_TIMEZONE, site.index.timeZone],
  );

  return (
    <div className="coast-feed-item">
      <div className="item-thumb">
        <MagicLink to={showUrl} context={slug}>
          <ResponsiveImage
            alt={summary.image.alt_text || summary.title}
            src={imgSrc}
            srcset={srcset}
            initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
            initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
          />
        </MagicLink>
      </div>

      <Heading level={3} className="item-title">
        <MagicLink to={showUrl} context={slug}>
          {summary.title}
        </MagicLink>
      </Heading>

      <section className="item-meta">
        <p>
          <time>{formattedDate}</time>
        </p>

        <CoastLinkedHost titleOnSameLine={true} item={item.record} />
        <CoastLinkedGuests titleOnSameLine={true} item={item.record} />
      </section>

      {/* Show rich text summary for upcoming shows */}
      {payload.fields?.summary?.value ? (
        <section className="item-summary" dangerouslySetInnerHTML={{ __html: payload.fields.summary.value }} />
      ) : (
        <section className="item-summary">{summary.description}</section>
      )}
    </div>
  );
});

export default CoastFeedUpcomingShowItem;
